// @catalog F.006
// ResumenView — vista por default del subnav de /ventas (Resumen).
// Extraída de DashboardVentas.tsx para respetar P7 (≤500 líneas/archivo).
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import AnalyticsService, {
  type MonthlyMetrics,
  type StoreMetrics,
  type TopProductMonthly,
} from "../../services/analytics.service";
import { SalesTimeline } from "./SalesTimeline";
import { TopProductsTable } from "./TopProductsTable";
import { RankingBars, type RankingItem } from "../analytics/RankingBars";
import { CompositionRow } from "../analytics/CompositionRow";

interface ResumenViewProps {
  monthlyData: MonthlyMetrics[];
  currentLabel: string;
  // F6: rango mostrado para el encabezado del SalesTimeline.
  rangeLabel: string;
  posRankingItems: RankingItem<StoreMetrics>[];
  voRankingItems: RankingItem<StoreMetrics>[];
  displayProductsCount: number;
  totalPOS: number;
  totalSO: number;
  current: MonthlyMetrics | null;
  onDrillStore: (s: StoreMetrics) => void;
  onSeeAllPOS: () => void;
  onSeeAllVendors: () => void;
  onSeeAllProducts: () => void;
  topProductsSlice: TopProductMonthly[];
  onProductClick?: (productId: number, productName: string) => void;
}

export function ResumenView(props: ResumenViewProps): JSX.Element {
  const {
    monthlyData,
    rangeLabel,
    posRankingItems,
    voRankingItems,
    totalPOS,
    totalSO,
    current,
    onDrillStore,
    onSeeAllPOS,
    onSeeAllVendors,
    onSeeAllProducts,
    topProductsSlice,
    onProductClick,
  } = props;

  const [selectedMonth, setSelectedMonth] = useLocalStorageState<string | null>(
    "villapps:ventas:selectedMonth",
    null,
  );

  const totalMonth = current?.salesAmount ?? 0;
  // Descuento del PERÍODO (suma sobre el rango), alineado con totalPOS/totalSO
  // que ya son del rango. Antes usaba current.totalDiscounts (solo el último mes
  // del rango) → cifra incoherente con el resto (BUG-004).
  const discounts = monthlyData.reduce(
    (s, m) => s + (m.totalDiscounts !== undefined ? m.totalDiscounts : 0),
    0,
  );
  // Denominador = facturación NETA (POS + SO). NO se suma el descuento: POS/SO ya
  // son netos (el descuento ya está contemplado en esos totales) — sumarlo aquí
  // lo contaría doble. El descuento se muestra como erosión sobre el neto.
  const compositionTotal = totalPOS + totalSO;
  const denomComposition = compositionTotal > 0 ? compositionTotal : totalMonth;

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
          gap: 2,
        }}
      >
        <SalesTimeline
          data={monthlyData}
          selectedMonth={selectedMonth}
          onMonthSelect={(key) =>
            setSelectedMonth((prev) => (prev === key ? null : key))
          }
          rangeLabel={rangeLabel}
        />
        <Card
          sx={{
            backgroundColor: "var(--surface)",
            border: "1px solid var(--border)",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Composición · {rangeLabel}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "var(--ink-3)", display: "block", mb: 2 }}
            >
              Cómo se reparte la facturación
            </Typography>
            <CompositionRow
              label="Tiendas POS"
              value={totalPOS}
              total={denomComposition}
              color="var(--accent)"
              formatValue={AnalyticsService.formatCurrency}
            />
            <CompositionRow
              label="Vendedores SO"
              value={totalSO}
              total={denomComposition}
              color="var(--brand-orange-2)"
              formatValue={AnalyticsService.formatCurrency}
            />
            {discounts > 0 && (
              <CompositionRow
                label="Descuentos aplicados"
                value={discounts}
                total={denomComposition}
                color="var(--neg)"
                inverse
                formatValue={AnalyticsService.formatCurrency}
              />
            )}
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Card
          sx={{
            backgroundColor: "var(--surface)",
            border: "1px solid var(--border)",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ mb: 1 }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Ranking tiendas POS
                </Typography>
                <Typography variant="caption" sx={{ color: "var(--ink-3)" }}>
                  Por facturación · {rangeLabel}
                </Typography>
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onSeeAllPOS}
                sx={{
                  fontSize: "12px",
                  color: "var(--ink-2)",
                  background: "none",
                  border: "1px solid var(--border)",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:hover": { backgroundColor: "var(--bg-2)" },
                }}
              >
                Ver todas
              </Box>
            </Stack>
            <RankingBars
              items={posRankingItems}
              formatValue={AnalyticsService.formatCurrency}
              onSelect={(it) => it.raw && onDrillStore(it.raw)}
              emptyMessage="Sin datos POS"
            />
          </CardContent>
        </Card>

        <Card
          sx={{
            backgroundColor: "var(--surface)",
            border: "1px solid var(--border)",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ mb: 1 }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Ranking vendedores SO
                </Typography>
                <Typography variant="caption" sx={{ color: "var(--ink-3)" }}>
                  Cuentas corporativas
                </Typography>
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onSeeAllVendors}
                sx={{
                  fontSize: "12px",
                  color: "var(--ink-2)",
                  background: "none",
                  border: "1px solid var(--border)",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:hover": { backgroundColor: "var(--bg-2)" },
                }}
              >
                Ver todos
              </Box>
            </Stack>
            <RankingBars
              items={voRankingItems}
              formatValue={AnalyticsService.formatCurrency}
              onSelect={(it) => it.raw && onDrillStore(it.raw)}
              emptyMessage="Sin datos vendedores"
            />
          </CardContent>
        </Card>
      </Box>

      <Card
        sx={{
          backgroundColor: "var(--surface)",
          border: "1px solid var(--border)",
          boxShadow: "none",
        }}
      >
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mb: 1 }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Top productos · {rangeLabel}
              </Typography>
              <Typography variant="caption" sx={{ color: "var(--ink-3)" }}>
                Por facturación total
              </Typography>
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onSeeAllProducts}
              sx={{
                fontSize: "12px",
                color: "var(--ink-2)",
                background: "none",
                border: "1px solid var(--border)",
                padding: "4px 8px",
                borderRadius: "6px",
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": { backgroundColor: "var(--bg-2)" },
              }}
            >
              Ver catálogo
            </Box>
          </Stack>
          <TopProductsTable
            products={topProductsSlice}
            {...(onProductClick && { onProductClick })}
          />
        </CardContent>
      </Card>
    </Stack>
  );
}
